import { useState, useEffect, useCallback, useRef } from "react";
import { Eye, X, ChevronLeft, ChevronRight, Loader2, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { generatePreview, generateMockInvoiceData } from "@/lib/previewGenerator";
import { getPageSize } from "@/lib/elements";

function PageCard({ html, css, pageSettings, pageNum, totalPages }) {
  const ps = pageSettings || {};
  const size = getPageSize(ps.pageSize, ps);
  const pageWidthPx = size.widthPx;
  const pageHeightPx = size.heightPx;
  const docHtml = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body {
    width: ${pageWidthPx}px;
    background: white;
    font-family: ${ps.fontFamily || "Arial, sans-serif"};
    font-size: ${ps.fontSize || 12}px;
    color: #111827;
    line-height: 1.4;
  }
  body {
    padding: ${ps.marginTop || 20}mm ${ps.marginRight || 20}mm ${ps.marginBottom || 20}mm ${ps.marginLeft || 20}mm;
    min-height: 100vh;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
  }
  /* The page-wrap from generatePreview is a flex column that fills the body,
     anchoring the bottom zone (page-footer / report-footer) to the bottom edge. */
  .preview-page-wrap { flex: 1 1 auto; display: flex; flex-direction: column; min-height: 0; }
  .preview-zone-body { flex: 1 1 auto; }
  table { border-collapse: collapse; }
  img { max-width: 100%; }
  ${css || ""}
</style>
</head>
<body>${html}</body>
</html>`;

  return (
    <div className="flex flex-col items-center">
      <p className="text-xs text-gray-400 mb-2 font-medium">
        Page {pageNum} of {totalPages} <span className="text-gray-500">· {size.label}</span>
      </p>
      <div
        style={{
          width: pageWidthPx,
          boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
          borderRadius: 2,
          overflow: "hidden",
          background: "white",
        }}
      >
        <iframe
          title={`page-${pageNum}`}
          srcDoc={docHtml}
          style={{
            width: pageWidthPx,
            height: pageHeightPx,
            border: "none",
            display: "block",
          }}
          sandbox="allow-same-origin"
        />
      </div>
    </div>
  );
}

export function PrintPreview({ sections, pageSettings, onClose }) {
  const [pageCount, setPageCount] = useState(1);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePage, setActivePage] = useState(0); // which page is in focus (for navigation)
  // Hidden iframe used as the print source. We build it on-demand so the
  // visible preview iframes stay decoupled (they use sandbox=allow-same-origin
  // which blocks print()).
  const printIframeRef = useRef(null);

  const generate = useCallback(() => {
    setLoading(true);
    setError(null);
    try {
      // Mock data is sized to exactly fill the target page count and overlays
      // variable-length description strings on the wrap key so the pagination
      // engine's character-aware line-counting math gets exercised. The
      // targetPages param is also passed to generatePreview so sheets are
      // produced even when the template has no lineItems element.
      const data = generateMockInvoiceData(sections, pageSettings, pageCount);
      const result = generatePreview(sections, pageSettings, data, pageCount);
      setPreviewData(result);
      setActivePage(0);
    } catch (e) {
      console.error("Preview error:", e);
      setError(e.message || "Failed to generate preview");
    } finally {
      setLoading(false);
    }
  }, [pageCount, sections, pageSettings]);

  useEffect(() => {
    generate();
  }, [generate]);

  // Clean up the hidden print iframe when the modal closes.
  useEffect(() => {
    return () => {
      const f = printIframeRef.current;
      if (f && f.parentNode) f.parentNode.removeChild(f);
    };
  }, []);

  const totalRenderedPages = previewData?.pages?.length || 0;

  /**
   * Open the browser's native Print dialog with all preview pages joined into
   * a single printable document. Users can choose "Save as PDF" as the
   * destination to export the layout without leaving the app.
   */
  const handlePrint = useCallback(() => {
    if (!previewData?.pages?.length) return;
    const ps = pageSettings;
    const size = getPageSize(ps.pageSize, ps);

    // Combine every preview page into one document, separated by hard
    // page-break wrappers so the print engine renders them as discrete sheets.
    const pagesHtml = previewData.pages.map((p, i) => `
      <div class="print-page" style="page-break-after: ${i === previewData.pages.length - 1 ? "auto" : "always"};">
        ${p}
      </div>`).join("\n");

    const doc = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Invoice Preview</title>
  <style>
    ${previewData.css}
    @page { size: ${size.cssPageRule}; margin: 2mm !important; }
    @media print {
      body { margin: 0; }
      .print-page { page-break-inside: auto; }
    }
  </style>
</head>
<body>${pagesHtml}</body>
</html>`;

    // (Re)create the hidden iframe each call so the loaded document is always
    // a fresh snapshot of the latest preview data.
    let frame = printIframeRef.current;
    if (frame && frame.parentNode) frame.parentNode.removeChild(frame);
    frame = document.createElement("iframe");
    frame.style.cssText = "position:fixed;left:-10000px;top:-10000px;width:0;height:0;border:0;";
    document.body.appendChild(frame);
    printIframeRef.current = frame;

    frame.onload = () => {
      try {
        frame.contentWindow.focus();
        frame.contentWindow.print();
      } catch (e) {
        console.error("Print failed:", e);
        window.alert("Couldn't open the print dialog. Try again or use your browser's File → Print.");
      }
    };
    frame.srcdoc = doc;
  }, [previewData, pageSettings]);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ background: "rgba(0,0,0,0.75)" }}
      data-testid="print-preview-modal"
    >
      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-4 shrink-0">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-gray-500" />
          <h2 className="font-heading font-bold text-sm text-gray-900">Print Preview</h2>
        </div>

        {/* Page count selector — drives mock data generation so the preview
            always renders exactly N pages with variable wrap-length items. */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <span className="text-[10px] text-gray-500 px-1">Sample data:</span>
          {[1, 2, 3, 4].map((n) => (
            <Button
              key={n}
              size="sm"
              variant={pageCount === n ? "default" : "ghost"}
              className="h-7 text-xs px-3"
              onClick={() => setPageCount(n)}
              data-testid={`preview-page-count-${n}`}
            >
              {n} Page{n > 1 ? "s" : ""}
            </Button>
          ))}
        </div>

        {/* Rendered page count info */}
        {!loading && totalRenderedPages > 0 && (
          <span className="text-[10px] text-gray-400">
            → {totalRenderedPages} page{totalRenderedPages > 1 ? "s" : ""} rendered
          </span>
        )}

        {/* Page navigation (when multiple rendered pages) */}
        {!loading && totalRenderedPages > 1 && (
          <div className="flex items-center gap-1 ml-auto mr-2">
            <Button
              size="icon"
              variant="outline"
              className="h-7 w-7"
              disabled={activePage === 0}
              onClick={() => setActivePage((p) => Math.max(0, p - 1))}
              data-testid="preview-prev-page"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <span className="text-xs text-gray-600 px-2">
              {activePage + 1} / {totalRenderedPages}
            </span>
            <Button
              size="icon"
              variant="outline"
              className="h-7 w-7"
              disabled={activePage === totalRenderedPages - 1}
              onClick={() => setActivePage((p) => Math.min(totalRenderedPages - 1, p + 1))}
              data-testid="preview-next-page"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}

        <Button
          variant="default"
          size="sm"
          className="h-8 ml-auto gap-1.5 bg-blue-600 hover:bg-blue-700 text-white"
          onClick={handlePrint}
          disabled={loading || !!error || totalRenderedPages === 0}
          data-testid="preview-print-btn"
          title="Print or Save as PDF — uses your browser's native print dialog"
        >
          <Printer className="w-3.5 h-3.5" />
          Print / Save as PDF
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onClose}
          data-testid="preview-close-btn"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* ── Body ── */}
      <div className="flex-1 overflow-auto py-8 px-4 flex justify-center" style={{ background: "#374151" }}>
        {loading && (
          <div className="flex flex-col items-center justify-center gap-3 text-white/70">
            <Loader2 className="w-8 h-8 animate-spin" />
            <p className="text-sm">Generating preview…</p>
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-50 text-red-700 rounded-xl p-6 max-w-lg text-sm">
            <p className="font-bold mb-1">Preview Error</p>
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && previewData && (
          <div className="flex flex-col gap-8 items-center pb-8">
            {/* Show all pages stacked, or just the active page based on toggle */}
            {previewData.pages.map((pageHtml, i) => (
              <PageCard
                key={i}
                html={pageHtml}
                css={previewData.css}
                pageSettings={pageSettings}
                pageNum={i + 1}
                totalPages={previewData.pages.length}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
